import { useParams, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import React, { useMemo, useCallback, useEffect } from "react";
import PageContent from "../PageContent";
import useLegislation from "@/hooks/useLegislation";
import DocumentSidebar from "@/components/content-sidebar/document-sidebar/document-sidebar";
import { DocumentTab } from "@/components/content-sidebar/document-sidebar/document-tab-triggers";
import { useLegislationViewerContent } from "@/hooks/useLegislationViewerContent";
import useConsolidationRenderingEnabled from "@/hooks/useConsolidationRenderingEnabled";
import {
  hasExplicitConsolidationUrlParams,
  parseConsolidationUrlParams,
  resolveLegislationConsolidationRenderTarget,
} from "@/lib/consolidationMetadata";
import { useConsolidationViewerParams } from "@/hooks/useConsolidationViewerParams";
import {
  getRetsinformationMetadataFromLegislation,
} from "@/lib/consolidationLegislationUtils";
import {
  documentHasDistinctOwnTimeline,
  isInTimelineUniverse,
  parseTimelineRootParam,
  parseTimelineScopeParam,
  parseTimelineAtParam,
  resolveShowingParentTimeline,
  resolveTimelineFetchExternalId,
} from "@/lib/timelineUniverseParams";
import DeprecatedConsolidationDatasourcePage from "./components/DeprecatedConsolidationDatasourcePage";
import { isDeprecatedSilverConsolidationDatasource } from "@/lib/deprecatedDatasources";
import LegislationDocumentContent from "./components/LegislationDocumentContent";
import PrintStyles from "@/partials/document-renderer/components/PrintStyles";
import { useLegislationTableStore } from "@/stores/legislation-table-state.store";
import { useDatasourceBreadcrumbReturn } from "@/pages/legislation/hooks/useDatasourceBreadcrumbReturn";
import { useDocumentReferenceStore } from "@/stores/document-reference.store";
import { getViewConfigFromPath } from "@/config/documentViewConfig";
import { getDisplayName, normalizeDataSource } from "@/config/datasources";
import { parseDocumentViewerHighlightParams } from "@/partials/document-renderer/utils/referenceHighlightQuery";
import { resolveLegislationViewerReturnUrl } from "@/lib/navigation/documentViewerReturn";
import { useDocumentViewerBreadcrumbs } from "@/hooks/useDocumentViewerBreadcrumbs";
import { useDocumentViewerStackSync } from "@/hooks/useDocumentViewerContextSync";
import AppRoutes from "@/constants/routes/AppRoutes";
import {
  ensureDatasourceFrame,
  getStackEntryRootKind,
} from "@/lib/navigation/documentNavigationStack";

/**
 * Unified component for rendering legislation HTML content.
 * Supports both chat context (from conversation references) and
 * legislation context (from legislation browse).
 *
 * The context is determined automatically from the route path.
 */
const UnifiedLegislationHTMLView: React.FC = () => {
  const { documentId, datasource } = useParams<{
    documentId: string;
    datasource: string;
  }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const config = useMemo(
    () => getViewConfigFromPath(location.pathname, "html"),
    [location.pathname]
  );

  const { setNavigateBack } = useLegislationTableStore();
  const { documentReferences } = useDocumentReferenceStore();

  const { useGetByExternalId, useGetTimelineByExternalId } = useLegislation();

  const normalizedDataSource = normalizeDataSource(datasource || "");
  const effectiveDatasource = normalizedDataSource || datasource || "";
  const isDeprecatedDatasource = isDeprecatedSilverConsolidationDatasource(
    datasource
  );

  const legislationQuery = useGetByExternalId(documentId, effectiveDatasource);

  const {
    enabled: consolidationFeatureEnabled,
    isReady: consolidationFlagsReady,
  } = useConsolidationRenderingEnabled();

  const consolidationUrlParams = useMemo(
    () => parseConsolidationUrlParams(searchParams),
    [searchParams]
  );

  const timelineRootParam = useMemo(
    () => parseTimelineRootParam(searchParams),
    [searchParams]
  );

  const timelineScope = useMemo(
    () => parseTimelineScopeParam(searchParams),
    [searchParams]
  );

  const timelineAtParam = useMemo(
    () => parseTimelineAtParam(searchParams),
    [searchParams]
  );

  const isInTimelineUniverseUrl = useMemo(
    () => isInTimelineUniverse(timelineRootParam, documentId),
    [documentId, timelineRootParam]
  );

  const legislation = legislationQuery.data ?? null;

  const isDocumentLoading = Boolean(
    documentId &&
      (legislationQuery.isPending ||
        (legislationQuery.isFetching &&
          legislation?.externalId !== documentId))
  );

  const hasHtml =
    Boolean(legislation?.html?.trim()) ||
    Boolean(legislation?.htmlWithIds?.trim());

  const retsinformationMeta = useMemo(() => {
    if (!legislation) return null;
    return getRetsinformationMetadataFromLegislation(legislation);
  }, [legislation]);

  const consolidationRenderTarget = useMemo(() => {
    if (!documentId) return null;
    return resolveLegislationConsolidationRenderTarget({
      documentId,
      urlParams: consolidationUrlParams,
      meta: retsinformationMeta,
      consolidationFeatureEnabled,
      timelineRoot: timelineRootParam,
    });
  }, [
    consolidationFeatureEnabled,
    consolidationUrlParams,
    documentId,
    retsinformationMeta,
    timelineRootParam,
  ]);

  useEffect(() => {
    if (config.context !== "legislation" || !documentId || !datasource) {
      return;
    }
    if (!legislationQuery.isFetched) return;

    if (legislationQuery.isError) {
      navigate(AppRoutes.legislationNotFound);
      return;
    }
    if (!legislation) return;
    if (hasHtml) return;
    if (consolidationRenderTarget) return;
    if (
      !consolidationFlagsReady &&
      !hasExplicitConsolidationUrlParams(consolidationUrlParams)
    ) {
      return;
    }

    navigate(AppRoutes.legislationPDF(documentId, datasource), {
      replace: true,
    });
  }, [
    config.context,
    consolidationRenderTarget,
    consolidationUrlParams,
    datasource,
    documentId,
    hasHtml,
    legislation,
    consolidationFlagsReady,
    legislationQuery.isError,
    legislationQuery.isFetched,
    navigate,
  ]);

  const showingParentTimeline = useMemo(
    () =>
      resolveShowingParentTimeline(
        timelineRootParam,
        documentId,
        timelineScope
      ),
    [documentId, timelineRootParam, timelineScope]
  );

  const timelineFetchId = useMemo(
    () =>
      resolveTimelineFetchExternalId(
        documentId,
        timelineRootParam,
        showingParentTimeline
      ),
    [documentId, timelineRootParam, showingParentTimeline]
  );

  const timelineQuery = useGetTimelineByExternalId(timelineFetchId);

  const ownTimelineQuery = useGetTimelineByExternalId(documentId ?? "", {
    enabled: isInTimelineUniverseUrl && Boolean(documentId),
  });

  const parentTimelineQuery = useGetTimelineByExternalId(
    timelineRootParam ?? "",
    {
      enabled: isInTimelineUniverseUrl && Boolean(timelineRootParam),
    }
  );

  const canSwapTimeline = useMemo(
    () =>
      isInTimelineUniverseUrl &&
      documentHasDistinctOwnTimeline(
        documentId,
        ownTimelineQuery.data,
        parentTimelineQuery.data
      ),
    [
      documentId,
      isInTimelineUniverseUrl,
      ownTimelineQuery.data,
      parentTimelineQuery.data,
    ]
  );

  const { setChangeLaw, updateParams } = useConsolidationViewerParams();

  // Version dropdown: pin the active consolidation to an activation date.
  // Deliberately keeps `change_law` (unlike `setAsOf`) so the viewer stays at
  // the same consolidation — only the rendered date changes.
  const setConsolidationVersionAsOf = useCallback(
    (asOf: string | null) => updateParams({ asOf }),
    [updateParams]
  );

  const {
    rawCSS,
    rawHTML,
    loading,
    error,
    isConsolidatedRender,
    isConsolidationLoading,
    isConsolidationNotFound,
    consolidationAsOf,
    consolidationResolvedAsOf,
    consolidationRenderContext,
    changedContainerIds,
    changedContainerGroupKeys,
    changedContainerStatusById,
    deletedContainerChanges,
  } = useLegislationViewerContent({
    legislation,
    documentId,
    datasource,
    urlParams: consolidationUrlParams,
    timelineRoot: timelineRootParam,
  });

  // References created from this view must reopen the consolidation, not the
  // plain anchor text. A 404 render falls back to the anchor, so nothing to pin.
  const consolidationLinkContext = useMemo(
    () =>
      isConsolidatedRender && !isConsolidationNotFound
        ? consolidationRenderContext
        : null,
    [isConsolidatedRender, isConsolidationNotFound, consolidationRenderContext]
  );

  useEffect(() => {
    if (config.context !== "legislation" || !datasource) {
      return;
    }
    if (getStackEntryRootKind() !== "kilder") {
      return;
    }
    ensureDatasourceFrame({
      label: getDisplayName(datasource) || datasource,
      url: AppRoutes.legislations,
      frameKind: "datasource",
      datasourceId: datasource.toLowerCase(),
    });
  }, [config.context, datasource, location.key]);

  const isProcessingIncomplete = useMemo((): boolean => {
    if (isConsolidatedRender && !isConsolidationNotFound) return false;
    if (!legislation) return false;
    const hasHtml = Boolean(
      legislation.html && legislation.html.trim().length > 0
    );
    const hasHtmlWithIds = Boolean(
      legislation.htmlWithIds && legislation.htmlWithIds.trim().length > 0
    );
    return hasHtml && !hasHtmlWithIds;
  }, [isConsolidatedRender, isConsolidationNotFound, legislation]);

  const {
    highlightIds,
    scrollToId,
    refMatchText,
    refChunkText,
    docSearchTerm,
  } = useMemo(
    () => parseDocumentViewerHighlightParams(searchParams),
    [searchParams]
  );

  const fallbackTitle = legislation?.title || config.defaultTitle;

  const resolveBaseReturnUrl = useCallback(
    () => resolveLegislationViewerReturnUrl(location.pathname, "html"),
    [location.pathname]
  );

  const handleExitToLegislationsList = useCallback(() => {
    if (config.context === "legislation") {
      setNavigateBack(true);
    }
  }, [config.context, setNavigateBack]);

  // Only the Kilder viewer has a source list to return to. The navigation
  // stack outlives a viewer-to-viewer navigation, so a chat viewer can be
  // shown a stale datasource crumb — which must not touch the Kilder table.
  const getDatasourceFrameOnClick = useDatasourceBreadcrumbReturn(
    config.context === "legislation" ? datasource : undefined
  );

  const { parentBreadcrumbs, flatParentBreadcrumbs, currentTitle, currentFullTitle, handleViewerBack } =
    useDocumentViewerBreadcrumbs({
      fallbackTitle,
      resolveBaseReturnUrl,
      onExitToList: handleExitToLegislationsList,
      getDatasourceFrameOnClick,
    });

  useDocumentViewerStackSync({
    title: fallbackTitle,
  });

  const shouldShowSidebar = useMemo(() => {
    if (!documentId) return false;
    if (config.conditionalSidebar) {
      return documentReferences.length > 0;
    }
    return true;
  }, [documentId, config.conditionalSidebar, documentReferences.length]);

  const renderSidebar = () => {
    if (!shouldShowSidebar || config.sidebarTabs.length === 0) {
      return undefined;
    }

    return (
      <DocumentSidebar
        key={documentId}
        contextualDefault={
          config.context === "chat"
            ? DocumentTab.CHAT_REFERENCES
            : undefined
        }
        availableTabs={config.sidebarTabs}
        documentId={documentId}
        selectionResetKey={documentId}
        legislation={legislation}
        tocItems={legislation?.tableOfContentsGold ?? undefined}
        createReferenceTarget={{
          documentId,
          source: effectiveDatasource,
          consolidation: consolidationLinkContext,
        }}
      />
    );
  };

  if (isDeprecatedDatasource && documentId) {
    return (
      <PageContent title="Kilde udfaset" contentClassName="flex flex-col !gap-0">
        <DeprecatedConsolidationDatasourcePage
          documentId={documentId}
          datasource={datasource ?? ""}
        />
      </PageContent>
    );
  }

  return (
    <>
      <PageContent
        title={currentTitle}
        titleFull={currentFullTitle}
        parent={parentBreadcrumbs}
        parentOverflowMenu={flatParentBreadcrumbs}
        onNavigateBack={handleViewerBack}
        rightSidebar={renderSidebar()}
        contentClassName="flex flex-col !gap-0"
      >
        <PrintStyles />
        <LegislationDocumentContent
          legislation={legislation}
          documentId={documentId}
          datasource={datasource}
          isDocumentLoading={isDocumentLoading}
          rawCSS={rawCSS}
          rawHTML={rawHTML}
          loading={loading}
          error={error}
          timelineItems={timelineQuery.data}
          isTimelineReady={timelineQuery.isFetched}
          highlightIds={highlightIds}
          scrollToId={scrollToId}
          highlightMatchText={refMatchText}
          highlightChunkText={refChunkText}
          documentSearchSeedTerm={docSearchTerm}
          isProcessingIncomplete={isProcessingIncomplete}
          isConsolidatedRender={isConsolidatedRender}
          isConsolidationLoading={isConsolidationLoading}
          isConsolidationNotFound={isConsolidationNotFound}
          consolidationAsOf={consolidationAsOf}
          consolidationResolvedAsOf={consolidationResolvedAsOf}
          consolidationRenderContext={consolidationRenderContext}
          changedContainerIds={changedContainerIds}
          changedContainerGroupKeys={changedContainerGroupKeys}
          changedContainerStatusById={changedContainerStatusById}
          deletedContainerChanges={deletedContainerChanges}
          consolidationFeatureEnabled={consolidationFeatureEnabled}
          timelineRootAccession={timelineRootParam}
          showingParentTimeline={showingParentTimeline}
          timelineScope={timelineScope}
          canSwapTimeline={canSwapTimeline}
          parentTimelineItems={parentTimelineQuery.data}
          ownTimelineItems={ownTimelineQuery.data}
          timelineTransitionKey={timelineFetchId}
          timelineAtParam={timelineAtParam}
          onConsolidationChangeLaw={setChangeLaw}
          onConsolidationAsOf={setConsolidationVersionAsOf}
        />
      </PageContent>
    </>
  );
};

export default UnifiedLegislationHTMLView;
